import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import GeneralButton from "../../common/GeneralButton";
import { TiChevronLeft, TiChevronRight, TiPlus } from "react-icons/ti";
import Icon from "../../common/Icon";
import CMSFormInput from "../../formFields/CMSFormInput";
import CMSFormTelephone from "../../formFields/CMSFormTelephone";
import { Formik } from "formik";
import { addressSchema } from "../../utils/validations";
import { useToast } from "../../contexts/ToastContext";
import Modal from "../../common/Modal";
import { useAddress } from "../../contexts/SubscriptionContext";
import { FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import CMSFormSelect from "../../formFields/CMSFormSelect";
import ContainerLoading from "../../common/loading/ContainerLoading";

const AddressManagement = () => {
  const {
    loading,
    fetchAddresses,
    savedAddresses,
    addAddress,
    editAddress,
    removeAddress,
    selectDefaultAddress,
  } = useAddress();

  const [state, setState] = useState(
    savedAddresses.length > 0 ? "list" : "add",
  );
  const navigate = useNavigate();
  const [editingAddress, setEditingAddress] = useState(null);
  const [deletingAddress, setDeletingAddress] = useState(null);
  const handleCloseModal = () => {
    setDeletingAddress(null);
    setError("");
  };
  const [error, setError] = useState("");
  const { addToast } = useToast();
  useEffect(() => {
    if (editingAddress) setState("add");
  }, [editingAddress]);
  useEffect(() => {
    fetchAddresses("order");
  }, []);
  useEffect(() => {
    if (savedAddresses.length > 0) {
      setState("list");
    } else {
      setState("add");
    }
  }, [savedAddresses]);
  const handleSubmit = async (values, resetForm) => {
    try {
      let success = false;

      if (editingAddress) {
        success = await editAddress(editingAddress._id, values);

        if (success) {
          addToast("Address updated successfully", "success");
          setEditingAddress(null);
        }
      } else {
        success = await addAddress(values);
        if (success) {
          addToast("Address added successfully", "success");
        }
      }

      if (success) {
        resetForm();
        setState("list");
      } else {
        setError("Failed to save address. Please try again.");
        setTimeout(() => {
          setError("");
        }, 5000);
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setError("Failed to save address. Please try again.");
      setTimeout(() => {
        setError("");
      }, 5000);
    }
  };

  const refs = {
    firstName: useRef(null),
    lastName: useRef(null),
    email: useRef(null),
    phone: useRef(null),
    address: useRef(null),
    landmark: useRef(null),
    city: useRef(null),
    country: useRef(null),
    isDefault: useRef(null),
  };
  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;

    // Smoothly scroll the nearest scrollable parent
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    // Give the scroll some time to finish, then focus
    setTimeout(() => {
      el.focus?.({ preventScroll: true });
    }, 500); // adjust based on your animation speed
  };
  return (
    <motion.div
      initial={{ x: -200, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -200, opacity: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="mt-6 flex flex-col gap-6 bg-[#DEE2DD]/40 rounded-[15px] p-6 lg:px-12 w-full relative"
    >
      <div className="grid grid-cols-1 lg:grid-cols-1  gap-8 items-start">
        <div>
          <div className=" w-full rounded-[15px]">
            {state === "list" && (
              <div className="flex items-center gap-4 mb-0">
                {/* <button
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 pe-3 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
                >
                  <FiArrowLeft size={30} />
                </button> */}
                <p className="text-24pt">Delivery Addresses</p>
              </div>
            )}
            {state === "add" && (
              <div className="flex items-center gap-4 mb-0">
                {/* <button
                  onClick={
                    savedAddresses.length > 0
                      ? () => {
                          setState("list");
                          setEditingAddress(null);
                        }
                      : () => navigate(-1)
                  }
                  className="flex items-center gap-2 pe-3 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
                >
                  <FiArrowLeft size={30} />
                </button> */}
                <p className="text-24pt">
                  {savedAddresses.length > 0
                    ? "Saved Addresses"
                    : "Add Address"}
                </p>
              </div>
            )}
            {loading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20 rounded-lg">
                <ContainerLoading />
              </div>
            )}
            {state === "list" && (
              <div className="grid grid-cols-1 gap-8 mt-8">
                {savedAddresses.map((addr, index) => {
                  return (
                    <div
                      key={index}
                      className={` transition-transform duration-300 ease-in-out
                      grid lg:grid-cols-[3.5fr_1fr] 2.5xl:grid-cols-[3.5fr_1fr] bg-white min-h-[120px] rounded-[15px] px-8 py-4 items-center `}
                      // onClick={() =>
                      //   selectDefaultAddress(
                      //     addr._id,
                      //     addr.isDefault === "1" ? "0" : "1"
                      //   )
                      // }
                    >
                      {/* <div className="flex items-center justify-start mb-3 md:mb-0">
                        <Icon
                          icon={isSelected ? "checbox" : "radio"}
                          className="w-[27px] h-[32px]"
                        />
                      </div> */}

                      <div className="flex flex-col items-start justify-center mb-5 md:mb-0">
                        <p className="text-24pt leading-tight">
                          {addr.firstName} {addr.lastName}
                        </p>
                        <p className="text-[18px] leading-none mt-1">
                          {[addr.addressLine1, addr.area, addr.landmark]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                        <p className="text-[18px] font-mono leading-tight mt-2">
                          {addr.city}, {addr.country}
                        </p>
                        <p className="text-18pt leading-tight text-green mt-2">
                          {addr.email}, {addr.mobile}
                        </p>
                        <div
                          className="flex items-start justify-end mt-4 cursor-pointer select-none"
                          onClick={() =>
                            selectDefaultAddress(
                              addr._id,
                              addr.isDefault === "0" ? "set" : "remove",
                            )
                          }
                        >
                          <div className="me-2">
                            <Icon
                              icon={
                                addr.isDefault === "1" ? "checked" : "checkbox"
                              }
                              className="w-[18px] h-auto"
                            />
                          </div>
                          <p className="text-green hover:text-secondary text-18pt font-mono">
                            Select as default address
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-8">
                        <Icon
                          icon="mapCross"
                          title="Click to remove address"
                          className="w-[27px] h-[32px] text-green hover:text-secondary cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingAddress(addr._id);
                          }}
                        />
                        <Icon
                          icon="edit"
                          title="Click to update address"
                          className="w-[32px] h-[30px] text-green hover:text-secondary cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingAddress(addr);
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-end">
                  <GeneralButton
                    onClick={() => {
                      setEditingAddress(null);
                      setState("add");
                    }}
                    text="Add New Address"
                    bgColor="#fff"
                    textColor="#295B24"
                    icon={<TiPlus size={24} />}
                    className="text-21pt px-4 brown"
                  />
                </div>
              </div>
            )}
            <Modal
              isOpen={deletingAddress != null}
              onClose={handleCloseModal}
              title="Remove Address"
              onConfirm={async () => {
                try {
                  const success = await removeAddress(deletingAddress);

                  if (success) {
                    handleCloseModal();
                    addToast("Address removed successfully", "success");
                  }
                } catch (err) {
                  const status = err?.response?.status;

                  if (status === 403) {
                    setError(
                      err?.response?.data?.message ||
                        "Sorry, this address can’t be deleted as it’s currently linked to a subscription.",
                    );
                  } else {
                    setError("Failed to remove address. Please try again.");
                  }

                  setTimeout(() => setError(""), 5000);
                }
              }}
            >
              <div className="font-mono">
                <p className="mt-2 font-medium">
                  Are you sure you want to remove this address?
                </p>

                {error && (
                  <p
                    className={`relative transition-all duration-300 ease-in-out w-full min-h-[35px] xl:min-h-[20px]  flex items-center justify-center text-center mb-4 ${
                      error
                        ? "opacity-100 translate-y-3"
                        : "opacity-0 -translate-y-2"
                    }`}
                  >
                    {error && (
                      <span className="errorMessage2  text-center w-full">
                        {error}
                      </span>
                    )}
                  </p>
                )}
              </div>
            </Modal>
            {state === "add" && (
              <div className="grid grid-cols-1 gap-12 mt-8">
                <Formik
                  initialValues={{
                    firstName: editingAddress?.firstName || "",
                    lastName: editingAddress?.lastName || "",
                    email: editingAddress?.email || "",
                    mobile: editingAddress?.mobile || "",
                    address: editingAddress?.addressLine1 || "",
                    landmark: editingAddress?.landmark || "",
                    area: editingAddress?.area || "",
                    city: editingAddress?.city || "",
                    country: editingAddress?.country || "UAE",
                    isDefault: editingAddress?.isDefault || "0",
                  }}
                  validationSchema={addressSchema}
                  onSubmit={(values, { resetForm }) => {
                    handleSubmit(values, resetForm);
                  }}
                >
                  {(form) => (
                    <div className="w-full">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <CMSFormInput
                          name="firstName"
                          label="First Name"
                          form={form}
                          ref={refs.firstName}
                          className="1"
                          required
                        />
                        <CMSFormInput
                          name="lastName"
                          label="Last Name"
                          form={form}
                          ref={refs.lastName}
                          className="1"
                          required
                        />
                        <CMSFormInput
                          name="email"
                          label="Email"
                          form={form}
                          ref={refs.email}
                          className="1"
                          required
                        />
                        <CMSFormTelephone
                          name="mobile"
                          label="Phone"
                          form={form}
                          ref={refs.phone}
                          className="1"
                          required
                        />
                        <CMSFormInput
                          name="address"
                          label="Address"
                          form={form}
                          ref={refs.address}
                          className="1"
                          required
                        />
                        <CMSFormInput
                          name="landmark"
                          label="Landmark"
                          form={form}
                          ref={refs.landmark}
                          className="1"
                          required
                        />

                        <CMSFormInput
                          name="area"
                          label="Area"
                          form={form}
                          className="1"
                          ref={refs.area}
                          required
                        />

                        <CMSFormSelect
                          name="city"
                          label="City"
                          selected={form.values.city}
                          form={form}
                          ref={refs.city}
                          className="1"
                          onChange={(e) => {
                            form.handleChange(e);
                          }}
                          placeholder={{ label: "Select City", value: "" }}
                          options={[
                            { label: "Abu Dhabi", value: "Abu Dhabi" },
                            { label: "Ajman", value: "Ajman" },
                            { label: "Dubai", value: "Dubai" },
                            { label: "Fujairah", value: "Fujairah" },
                            {
                              label: "Ras Al Khaimah",
                              value: "Ras Al Khaimah",
                            },
                            { label: "Sharjah", value: "Sharjah" },
                            { label: "Umm Al Quwain", value: "Umm Al Quwain" },
                          ]}
                          required
                        />
                        <CMSFormInput
                          name="country"
                          label="Country"
                          form={form}
                          ref={refs.country}
                          className="1"
                          disabled
                          required
                        />
                      </div>

                      <div
                        ref={refs.isDefault}
                        className="flex items-center justify-start mt-10 cursor-pointer"
                        onClick={() =>
                          form.setFieldValue(
                            "isDefault",
                            form.values.isDefault === "1" ? "0" : "1",
                          )
                        }
                      >
                        <div className="w-[38px] h-[26px] me-5 flex items-center justify-center">
                          <Icon
                            icon={
                              form.values.isDefault === "1"
                                ? "checked"
                                : "checkbox"
                            }
                            className="w-[30px] h-auto"
                          />
                        </div>
                        <p className="text-green text-18pt md:text-21pt select-none">
                          Save as default address
                        </p>
                      </div>
                      {error && (
                        <div
                          className={`relative transition-all duration-300 ease-in-out mt-3 ${
                            error
                              ? "opacity-100 translate-y-0"
                              : "opacity-0 -translate-y-2"
                          }`}
                        >
                          {error && (
                            <span className="errorMessage">{error}</span>
                          )}
                        </div>
                      )}
                      <div className="flex flex-col-reverse md:flex-row items-center justify-between mt-10 gap-5">
                        <div></div>
                        <GeneralButton
                          onClick={() => {
                            form.handleSubmit();
                            setTimeout(
                              () => scrollToFirstError(form?.errors),
                              100,
                            );
                          }}
                          text={
                            editingAddress ? "Update Address" : "Add Address"
                          }
                          type={"submit"}
                          iconPosition="Right"
                          textColor="#fff"
                          icon={<TiPlus size={24} />}
                          className="text-21pt px-4"
                        />
                      </div>
                    </div>
                  )}
                </Formik>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AddressManagement;
